import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FormProvider, RHFTextField } from '../../components/hook-form';
import { fNumber } from '../../utils/formatNumber';
import CardRedemptionDialogFrame from './CardRedemptionDialogFrame';

const DISPUTE_REASONS = [
  { value: '', label: '-- Select Reason --', prefill: '' },
  {
    value: 'wrong_face_value',
    label: 'Wrong Face Value',
    prefill: 'The card was submitted with an incorrect face value.',
  },
  {
    value: 'market_price_changed',
    label: 'Market Price Changed',
    prefill: 'The market price has changed since the card was submitted.',
  },
  { value: 'other', label: 'Other Reason', prefill: '' },
];

const disputeSchema = Yup.object().shape({
  amount: Yup.number().typeError('Card amount must be a number').required('Card amount is required'),
  number_of_cards: Yup.number()
    .typeError('Number of cards must be a number')
    .integer('Number of cards must be an integer')
    .min(1, 'At least 1 card is required')
    .required('Number of cards is required'),
  reason: Yup.string().required('Please select a reason'),
  remark: Yup.string().when('reason', {
    is: 'other',
    then: Yup.string().required('Remark is required when "Other Reason" is selected'),
    otherwise: Yup.string(),
  }),
  settlement_amount: Yup.string().required('Settlement amount is required'),
  confirm: Yup.boolean().oneOf([true], 'You must confirm the changes'),
});

function formatCurrencyField(value) {
  const normalizedValue = String(value || '').replaceAll(',', '').trim();

  if (!normalizedValue) {
    return '';
  }

  const numericValue = Number(normalizedValue);

  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export default function DisputeOrderDialog({ loading, onClose, onSubmit, open, order }) {
  const methods = useForm({
    resolver: yupResolver(disputeSchema),
    defaultValues: {
      amount: fNumber(order?.amount || 0),
      reason: '',
      remark: '',
      number_of_cards: order?.number_of_cards || '',
      settlement_amount: fNumber(order?.settlement_amount || 0),
      confirm: false,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    reset({
      amount: fNumber(order?.amount || 0),
      reason: '',
      remark: '',
      number_of_cards: order?.number_of_cards || '',
      settlement_amount: fNumber(order?.settlement_amount || 0),
      confirm: false,
    });
  }, [open, order, reset]);

  useEffect(() => {
    const selectedReason = DISPUTE_REASONS.find((reason) => reason.value === values.reason);
    if (selectedReason && selectedReason.value !== 'other') {
      setValue('remark', selectedReason.prefill, { shouldValidate: true });
    } else if (selectedReason?.value === 'other') {
      setValue('remark', '', { shouldValidate: true });
    }
  }, [setValue, values.reason]);

  const submitForm = (formValues) =>
    onSubmit({
      ...formValues,
      amount: String(formValues.amount).replaceAll(',', ''),
      settlement_amount: String(formValues.settlement_amount).replaceAll(',', ''),
    });

  return (
    <CardRedemptionDialogFrame
      open={open}
      onClose={onClose}
      title="File a Dispute"
      actions={
        <LoadingButton
          variant="contained"
          color="warning"
          loading={loading || isSubmitting}
          onClick={handleSubmit(submitForm)}
        >
          Submit Dispute
        </LoadingButton>
      }
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(submitForm)}>
        <Card sx={{ p: { xs: 1, sm: 3 }, borderRadius: 2, boxShadow: 'none' }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {order?.category} - {order?.type_name} ({order?.country})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Order ID: {order?.order_id}
            </Typography>
            <Divider sx={{ my: 1 }} />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <b>Note:</b> Use this option when the submitted card details need correction before the order can proceed.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <RHFTextField
                  name="amount"
                  label={`Total Card Amount (${order?.currency || ''})`}
                  required
                  sx={{ mb: 2 }}
                  error={!!errors.amount}
                  helperText={errors.amount?.message || 'Enter the corrected total card amount.'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RHFTextField
                  name="number_of_cards"
                  label="Number of Cards"
                  required
                  sx={{ mb: 2 }}
                  error={!!errors.number_of_cards}
                  helperText={errors.number_of_cards?.message || 'Number of valid uploaded cards.'}
                />
              </Grid>
            </Grid>

            <RHFTextField
              name="settlement_amount"
              label={`Settlement Amount (${order?.settlement_currency || 'NGN'})`}
              onFocus={(event) => {
                event.currentTarget.select();
              }}
              onBlur={(event) => {
                setValue('settlement_amount', formatCurrencyField(event.currentTarget.value), {
                  shouldValidate: true,
                });
              }}
              required
              sx={{ mb: 2 }}
              error={!!errors.settlement_amount}
              helperText={
                errors.settlement_amount?.message ||
                'Settlement amount after dispute resolution.'
              }
            />

            <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.reason}>
              <InputLabel id="card-redemption-dispute-reason-label">Select Reason</InputLabel>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <Select {...field} labelId="card-redemption-dispute-reason-label" label="Select Reason">
                    {DISPUTE_REASONS.map((reason) => (
                      <MenuItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.reason && <FormHelperText>{errors.reason.message}</FormHelperText>}
            </FormControl>

            <RHFTextField
              name="remark"
              label="Remark"
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              error={!!errors.remark}
              helperText={
                errors.remark?.message ||
                'This note is recorded with the dispute update and can be edited before submission.'
              }
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={values.confirm}
                  onChange={(event) => setValue('confirm', event.target.checked, { shouldValidate: true })}
                />
              }
              label="I confirm these values should replace the current order details"
              sx={{ alignItems: 'flex-start' }}
            />
            {errors.confirm && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {errors.confirm.message}
              </Typography>
            )}
          </Box>
        </Card>
      </FormProvider>
    </CardRedemptionDialogFrame>
  );
}

DisputeOrderDialog.propTypes = {
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
};
